import { create } from 'zustand'
import { apiClient } from '@/lib/api'
import type { AdminUser } from '@/types'

interface LoginCredentials {
  username: string
  password: string
}

interface ProfileResponse {
  admin: AdminUser
  permissions: string[]
}

interface AuthState {
  user: AdminUser | null
  /** 后端返回的权限码集合;super_admin 持有 ["*"]。 */
  permissions: string[]
  loading: boolean
  error: string | null
  login: (credentials: LoginCredentials) => Promise<{ success: boolean; user?: AdminUser; error?: string }>
  logout: () => Promise<void>
  /** 仅清掉本地登录态(不调 logout API)。
   *  用于"改完密码本地 token 已失效"的场景,避免触发 apiClient 的 401 副作用。 */
  clearLocal: () => void
  /** 启动 / 路由守卫调用;有 token 就拉一次 /profile 校准。 */
  checkAuth: () => Promise<void>
  /** 主动刷新 profile(权限分配 / 角色变更后调用)。 */
  refreshProfile: () => Promise<void>
  setUser: (user: AdminUser | null) => void
}

const LS_USER_KEY = 'admin_user'
const LS_PERMS_KEY = 'admin_permissions'

function loadCachedPermissions(): string[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = localStorage.getItem(LS_PERMS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function persistAuth(user: AdminUser | null, permissions: string[]) {
  if (typeof window === 'undefined') return
  if (user) {
    localStorage.setItem(LS_USER_KEY, JSON.stringify(user))
    localStorage.setItem(LS_PERMS_KEY, JSON.stringify(permissions))
  } else {
    localStorage.removeItem(LS_USER_KEY)
    localStorage.removeItem(LS_PERMS_KEY)
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  permissions: loadCachedPermissions(),
  loading: true,
  error: null,

  setUser: (user) => set({ user }),

  login: async (credentials) => {
    set({ loading: true, error: null })

    try {
      const response = await apiClient.post<{
        admin: AdminUser
        access_token: string
        refresh_token: string
      }>('/admin-api/v1/admin/auth/login', credentials)

      if (response.code === 0) {
        const { admin, access_token } = response.data
        apiClient.setToken(access_token)
        // 先用 admin 占位 + 空权限,随后立刻拉一次 profile 拿权限码,
        // 避免登录后第一次菜单渲染因权限未到位而被全部过滤。
        persistAuth(admin, [])
        set({ user: admin, permissions: [], loading: false, error: null })
        try {
          await get().refreshProfile()
        } catch (e) {
          // refreshProfile 已经处理过错误日志,这里继续返回 success。
          console.warn('refreshProfile after login failed:', e)
        }
        return { success: true, user: admin }
      }
      throw new Error(response.message)
    } catch (error: any) {
      console.error('登录异常:', error)
      const errorMessage = error.message || '登录失败'
      set({ loading: false, error: errorMessage })
      return { success: false, error: errorMessage }
    }
  },

  logout: async () => {
    try {
      await apiClient.post('/admin-api/v1/admin/auth/logout')
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      apiClient.clearToken()
      persistAuth(null, [])
      set({ user: null, permissions: [], loading: false, error: null })
    }
  },

  clearLocal: () => {
    apiClient.clearToken()
    persistAuth(null, [])
    set({ user: null, permissions: [], loading: false, error: null })
  },

  checkAuth: async () => {
    const token = apiClient.getToken()
    if (!token) {
      set({ loading: false, user: null, permissions: [] })
      return
    }

    // 先用 localStorage 里的快照让 UI 立即可用,
    // 再异步拉 /profile 校准。后端不可用时仍能进首页避免被踢回登录页。
    if (typeof window !== 'undefined') {
      const userStr = localStorage.getItem(LS_USER_KEY)
      if (userStr) {
        try {
          const cachedUser = JSON.parse(userStr) as AdminUser
          set({ user: cachedUser, permissions: loadCachedPermissions(), loading: false, error: null })
        } catch (error) {
          console.error('解析用户信息失败:', error)
        }
      }
    }

    try {
      const response = await apiClient.get<ProfileResponse>('/admin-api/v1/admin/auth/profile')
      if (response.code === 0) {
        const { admin, permissions } = response.data
        persistAuth(admin, permissions || [])
        set({ user: admin, permissions: permissions || [], loading: false, error: null })
      } else {
        // token 已经无效,清理本地状态,路由守卫会把用户带回登录页。
        apiClient.clearToken()
        persistAuth(null, [])
        set({ loading: false, user: null, permissions: [] })
      }
    } catch (error) {
      // 网络错误时保留 localStorage 快照,不强制登出 — 避免离线场景下被反复踢出。
      console.warn('refreshProfile via checkAuth failed, keeping cached state:', error)
      set({ loading: false })
    }
  },

  refreshProfile: async () => {
    try {
      const response = await apiClient.get<ProfileResponse>('/admin-api/v1/admin/auth/profile')
      if (response.code === 0) {
        const { admin, permissions } = response.data
        persistAuth(admin, permissions || [])
        set({ user: admin, permissions: permissions || [], error: null })
      } else if (response.code === 401) {
        apiClient.clearToken()
        persistAuth(null, [])
        set({ user: null, permissions: [], error: response.message || 'unauthorized' })
      }
    } catch (error: any) {
      console.error('refreshProfile failed:', error)
      throw error
    }
  },
}))

/**
 * 判断当前已登录管理员是否拥有 code 权限。
 * super_admin 会以 "*" 通配满足任何权限。
 */
export function hasPermission(code: string): boolean {
  const { permissions } = useAuthStore.getState()
  if (!permissions || permissions.length === 0) return false
  if (permissions.includes('*')) return true
  return permissions.includes(code)
}

/** 任一权限码命中即视为有权。 */
export function hasAnyPermission(codes: string[]): boolean {
  if (!codes || codes.length === 0) return true
  return codes.some((c) => hasPermission(c))
}

/** 所有权限码都需要命中。 */
export function hasAllPermissions(codes: string[]): boolean {
  if (!codes || codes.length === 0) return true
  return codes.every((c) => hasPermission(c))
}
